import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/common/PageHeader";
import { SurfaceCard } from "@/components/common/Cards";
import { Progress } from "@/components/ui/progress";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Billing() {
  const [u, setU] = useState(null);
  const [count, setCount] = useState(10);
  const [busy, setBusy] = useState(false);
  const [sp, setSp] = useSearchParams();
  const load = () => api.get("/billing/usage").then((r) => setU(r.data)).catch(() => {});
  useEffect(() => {
    const sid = sp.get("topup_session");
    if (!sid) { load(); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/topup/status/${sid}`);
        if (data.status === "paid") { toast.success(`${data.count} consultations added`); setSp({}); load(); return; }
        if (data.status === "expired" || tries > 10) { toast.error("Payment not completed"); setSp({}); load(); return; }
      } catch { load(); return; }
      tries += 1; setTimeout(poll, 2000);
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = u?.currency === "USD" ? "$" : `${u?.currency || ""} `;
  const buy = async () => {
    setBusy(true);
    try { const { data } = await api.post("/billing/topup", { count, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't start checkout"); setBusy(false); }
  };
  return (
    <div data-testid="billing-page">
      <PageHeader title="Consultation credits" subtitle="Your plan's consultation balance. Each treatment-plan generation uses one." />
      {!u ? <Loader2 className="h-6 w-6 animate-spin text-[#00A85A]" /> : (
        <div className="grid gap-4 md:grid-cols-2">
          <SurfaceCard className="p-6" data-testid="billing-usage">
            <div className="text-sm text-[#8B9698]">{u.plan_name || "Current plan"}</div>
            <div className="mt-1 text-3xl font-extrabold text-[#101516] dark:text-white" data-testid="billing-usage-text">{u.unlimited ? `${u.used} used · no limit` : `Used ${u.used} of ${u.limit}`}</div>
            {!u.unlimited && <><Progress value={(u.used / Math.max(u.limit, 1)) * 100} className="mt-4" /><div className="mt-2 text-sm text-[#687173]" data-testid="billing-remaining">{u.remaining} remaining · {u.included} included + {u.extra} extra</div></>}
          </SurfaceCard>
          <SurfaceCard className="p-6" data-testid="billing-topup">
            <h3 className="font-bold text-[#101516] dark:text-white">Add consultations</h3>
            {u.extra_price > 0 ? (<>
              <div className="mt-3 flex items-center gap-3">
                <select value={count} onChange={(e) => setCount(Number(e.target.value))} className="h-10 rounded-lg border border-[#E2E8F0] bg-white px-2 dark:border-[#223139] dark:bg-white/5" data-testid="billing-topup-count">
                  {Array.from({ length: 20 }, (_, i) => (i + 1) * 10).map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <span className="text-sm text-[#687173]">× {cur}{Number(u.extra_price).toFixed(2)} = <b data-testid="billing-topup-total">{cur}{(count * u.extra_price).toFixed(2)}</b></span>
              </div>
              <Button onClick={buy} disabled={busy} className="mt-4 bg-[#00A85A] text-white hover:bg-[#008F4D]" data-testid="billing-topup-buy">{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}Buy {count} consultations</Button>
            </>) : <p className="mt-2 text-sm text-[#8B9698]">Extra consultations aren't available for your plan yet. Please contact the admin.</p>}
          </SurfaceCard>
        </div>
      )}
    </div>
  );
}
